import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import DirectionsIcon from "@mui/icons-material/Directions";
import CropFreeIcon from "@mui/icons-material/CropFree";
import SearchIcon from "@mui/icons-material/Search";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import * as colors from "@mui/material/colors";
import { Category } from "./category";
import { useDispatch, useSelector } from "react-redux";
import {
  searchByCategoryStart,
  searchByNameStart,
  suggestionFetchStart,
} from "../../store/map/reducer";

export const Search = ({ setActiveTab = () => { }, toggleDrawer = () => { } }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [isCategoryOpen, setCategoryOpen] = useState(false);
  const [selected, setSelected] = useState();
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const { suggestions, fetchSuggestionLoading: loading } = useSelector(
    (state) => state.map
  );

  // CATEGORY SEARCH
  useEffect(() => {
    if (selected) {
      let bbox;
      if (window.initialMap) {
        bbox = window.initialMap
          .getView()
          .calculateExtent(window.initialMap.getSize())
          .join(",");
      }

      dispatch(
        searchByCategoryStart({
          category: selected,
          bbox,
        })
      );
    }
  }, [selected]);

  // NORMAL SEARCH
  const handleSearch = (value) => {
    if (!value) return;

    let bbox;
    if (window.initialMap) {
      bbox = window.initialMap
        .getView()
        .calculateExtent(window.initialMap.getSize())
        .join(",");
    }

    // If it's a city or region, perhaps search differently, but for now, use label
    const searchTerm = value.label || value;

    dispatch(
      searchByNameStart({
        name: searchTerm,
        bbox,
      })
    );
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", width: "100%", gap: 1 }}>
        <Box
          sx={{
            display: "flex",
            flex: 1,
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: colors.common.white,
            borderRadius: 1,
            boxShadow: 1,
            minWidth: 0, // important for flex children to shrink
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <IconButton size="large" onClick={toggleDrawer}>
              <MenuIcon />
            </IconButton>
            <Box width="1px" height="30px" bgcolor={colors.grey[400]} />
          </Box>

          {/* ---- AUTOCOMPLETE ---- */}
          <Autocomplete
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            options={suggestions}
            loading={loading}
            popupIcon={false}
            sx={{ flex: 1 }}
            slotProps={{
              popper: {
                sx: {
                  minWidth: '400px',
                  marginTop:'8px !important',
                },
              },
            }}
            filterOptions={(x) => x} // important: do NOT filter again
            getOptionLabel={(option) => option.label || ""}
            onChange={(_, value) => {
              if (value?.label) {
                handleSearch(value);
              }
            }}
            inputValue={inputValue}
            onInputChange={(event, value, reason) => {
              setInputValue(value);

              if (reason === "clear") {
                setOpen(false);
                return;
              }

              if (value.length === 0) {
                setOpen(false);
                return;
              }

              setOpen(true);

              let bbox;
              if (window.initialMap) {
                bbox = window.initialMap
                  .getView()
                  .calculateExtent(window.initialMap.getSize())
                  .join(",");
              }

              dispatch(suggestionFetchStart({ name: value, bbox }));
            }}
            renderOption={(props, option) => {
              let subtitle = '';
              if (option.type === 'city_name') {
                subtitle = t('City');
              } else if (option.type === 'region') {
                subtitle = t('Region');
              } else {
                const city = option.city_name || '';
                const region = option.region || '';
                if (city && region) {
                  subtitle = `${region} ${t('Region')}, ${city} ${t('City')}`;
                } 
                else if (city) {
                  subtitle = `${city}, ${t('City')}`;
                } else if (region) {
                  subtitle = `${region} ${t('Region')}`;
                }
              }
              return (
                <li {...props} key={option.id}>
                  <Box>
                    <Typography variant="body1">{option.label}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {subtitle}
                    </Typography>
                  </Box>
                </li>
              );
            }}
            renderInput={(params) => (
              <Box sx={{ display: "flex", alignItems: "center", paddingLeft: 1 }}>
                <SearchIcon sx={{ color: colors.grey[600] }} />
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder={`${t('PlaceName')}, ${t('Address')}`}
                  size="small"
                  sx={{
                    width: "100%",
                    border: "none",
                    "& fieldset": { border: "none" },
                  }}
                  onFocus={() => setCategoryOpen(true)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      handleSearch({ label: inputValue });
                    }
                  }}
                />
              </Box>
            )}
          />
        </Box>

        {/* BUTTONS */}
        <Tooltip title={t('Direction')}>
          <Box sx={{ backgroundColor: colors.blue[800], borderRadius: 1 }}>
            <IconButton size="large" onClick={() => setActiveTab("DIRECTION")}>
              <DirectionsIcon sx={{ color: colors.common.white }} />
            </IconButton>
          </Box>
        </Tooltip>

        <Tooltip title={t('SearchNearby')}>
          <Box sx={{ backgroundColor: colors.blue[800], borderRadius: 1 }}>
            <IconButton size="large" onClick={() => setActiveTab("NEAR_BY")}>
              <LocationOnIcon sx={{ color: colors.common.white }} />
            </IconButton>
          </Box>
        </Tooltip>

        <Tooltip title={t('ServiceArea')}>
          <Box sx={{ backgroundColor: colors.blue[800], borderRadius: 1 }}>
            <IconButton size="large" onClick={() => setActiveTab("SERVICE_AREA")}>
              <CropFreeIcon sx={{ color: colors.common.white }} />
            </IconButton>
          </Box>
        </Tooltip>
      </Box>

      {/* CATEGORY POPUP */}
      <Box sx={{ display: isCategoryOpen ? "block" : "none" }}>
        <Category
          title={t('SearchBy')}
          selected={selected}
          onSelect={(category) => setSelected(category)}
          onClose={() => setCategoryOpen(false)}
          setActiveTab={setActiveTab}
        />
      </Box>
    </Box>
  );
};
